import { baseConfig } from '@adornis/config/baseConfig.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { whitelistConfig } from '@nsc/whitelist/config.js';
import { html, nothing } from 'lit';
import { useNexusPermissionAccess } from '../../features/permissions/use-nexus-permission.js';

export const NEXUS_DASHBOARD_COLUMN_CLASS = 'max-w-6xl mx-auto px-3 sm:px-4';

type DashboardLinkCardOpts = {
  title: string;
  description?: string;
  href: string;
  buttonLabel: string;
};

/** Shared “card + primary outline button” block for dashboard shortcuts. */
export function nexusDashboardLinkCard(opts: DashboardLinkCardOpts) {
  return html`
    <section class="card bg-base-100 shadow-xl rounded-xl min-w-64 border border-neutral/10">
      <div class="card-body flex flex-col gap-2">
        <h2 class="card-title text-xl font-bold text-neutral">${opts.title}</h2>
        ${opts.description ? html`<p class="text-base text-neutral/60">${opts.description}</p>` : nothing}
        <a class="btn btn-outline btn-primary mt-2 w-fit" href=${opts.href}>${opts.buttonLabel}</a>
      </div>
    </section>
  `;
}

/** Anyone with {@link NexusPermission.WhitelistManage}; not gated by UserManage. */
export const DashboardWhitelistSection = stateful(() => {
  const { can, loading } = useNexusPermissionAccess(NexusPermission.WhitelistManage);
  if (loading || !can) return nothing;

  const ticketOn = whitelistConfig.get('TICKET_WHITELIST');
  const agentOn = whitelistConfig.get('AGENT_WHITELIST');

  return html`
    <div class="${NEXUS_DASHBOARD_COLUMN_CLASS} pb-6 mt-6">
      <section class="card bg-primary/5 shadow-2xl rounded-2xl border border-primary/20">
        <div class="card-body py-8">
          <h2 class="card-title text-2xl font-extrabold text-primary mb-2">Testing: Whitelists</h2>
          <p class="text-base text-base-content/70 mb-2">
            Tickets are processed when they match <span class="font-semibold">either</span> of the following whitelists:
          </p>
          <div class="flex flex-col gap-4 mb-2">
            ${whitelistToggleRow('Ticket Whitelist', ticketOn)} ${whitelistToggleRow('Human Agent Whitelist', agentOn)}
          </div>
          <div class="mt-4">
            <a class="btn btn-outline btn-primary mt-2 w-fit" href="/whitelist">Manage Whitelists</a>
          </div>
        </div>
      </section>
    </div>
  `;
});

function whitelistToggleRow(label: string, on: boolean) {
  return html`
    <div class="flex items-center justify-between gap-4 flex-wrap">
      <span class="font-semibold text-base-content text-lg">${label}</span>
      <div class="flex items-center gap-3">
        <input type="checkbox" class="toggle toggle-primary scale-125 pointer-events-none" .checked=${on} />
        <span class="text-sm text-base-content/60">${on ? 'Enabled' : 'Disabled'}</span>
      </div>
    </div>
  `;
}

/** “Operations” shortcuts; each card is gated by its own permission. */
export const DashboardOperationsSection = stateful(() => {
  const { can: canUserManage, loading: loadingUserManage } = useNexusPermissionAccess(NexusPermission.UserManage);
  const { can: canClient, loading: loadingClient } = useNexusPermissionAccess(NexusPermission.ClientManage);
  const { can: canIntro, loading: loadingIntro } = useNexusPermissionAccess(NexusPermission.IntrospectionView);
  const { can: canArticle, loading: loadingArticle } = useNexusPermissionAccess(NexusPermission.ArticleGeneration);
  const { can: canPartnerScheduling, loading: loadingPartnerScheduling } = useNexusPermissionAccess(
    NexusPermission.PartnerSchedulingClientToggle,
  );
  const { can: canPartnerImport, loading: loadingPartnerImport } = useNexusPermissionAccess(
    NexusPermission.PartnerSchedulingImport,
  );

  const loading =
    loadingUserManage || loadingClient || loadingIntro || loadingArticle || loadingPartnerScheduling || loadingPartnerImport;
  if (loading) return nothing;

  const showArticle = canArticle && baseConfig.get('DEPLOYMENT') === 'development';
  const showPartnerScheduling = canPartnerScheduling || canPartnerImport;
  const hasAnyCard = canUserManage || canClient || showArticle || canIntro || showPartnerScheduling;
  if (!hasAnyCard) return nothing;

  return html`
    <div class="divider max-w-6xl mx-auto my-2">Operations</div>
    <div class="flex flex-col gap-8 ${NEXUS_DASHBOARD_COLUMN_CLASS} pb-10">
      ${canUserManage
        ? nexusDashboardLinkCard({
            title: 'Users & roles',
            description: 'Manage Nexus users, role assignments, and platform roles.',
            href: '/admin',
            buttonLabel: 'Open admin',
          })
        : nothing}
      ${canClient
        ? nexusDashboardLinkCard({
            title: 'Clients',
            description: 'Manage the clients and their SLA types.',
            href: '/client',
            buttonLabel: 'Manage Clients',
          })
        : nothing}
      ${showArticle
        ? nexusDashboardLinkCard({
            title: 'Article Generation',
            description: 'Create knowledge base articles from ticket information.',
            href: '/article-generation',
            buttonLabel: 'Generate Article',
          })
        : nothing}
      ${canIntro
        ? nexusDashboardLinkCard({
            title: 'Introspection',
            href: '/introspection',
            buttonLabel: 'View Process Introspection',
          })
        : nothing}
      ${showPartnerScheduling
        ? nexusDashboardLinkCard({
            title: 'Partner scheduling',
            description: 'Manage partner scheduling clients and import partner network workbooks.',
            href: '/partner-scheduling/clients',
            buttonLabel: 'Open partner scheduling',
          })
        : nothing}
    </div>
  `;
});
